// @ts-check
// Cliente de servicio para el catálogo público de ejercicios
// (MOD-EXERCISE-BANK, TSK-0054, docs/03-frontend.md §9.1). Como todo
// servicio, no toca el DOM y usa exclusivamente `apiRequest` de
// `api-client.js` -- nunca `fetch` directamente.
//
// El mapeo de campos de este módulo se limita a los que
// `EjercicioPublico` (backend/src/app/schemas/exercise.py) declara
// realmente: `id`, `slug`, `titulo`, `descripcion`, `dificultad`,
// `categoria`, `tiempo_orientativo_min`, `puntuacion_xp`, `casos_prueba`,
// `etiquetas`, `contenido_pedagogico`. No se inventan campos que ese
// schema no declare (p.ej. nada de `rubrica`/`contenido_docente`, que ni
// siquiera existen en la proyección pública -- ver DEC-016 en ese schema).

import { apiRequest } from './api-client.js';

/**
 * @typedef {{
 *   entrada: string,
 *   salidaEsperada: string,
 *   esLimite: boolean,
 *   descripcion: string|null,
 * }} CasoPruebaVisible
 */

/**
 * @typedef {{
 *   contexto: string,
 *   conceptosPrevios: string[],
 *   objetivos: string[],
 *   restricciones: string[],
 *   ejemplos: string[],
 *   erroresFrecuentes: string[],
 *   unidadDidactica: string|null,
 *   competencias: string[],
 *   requisitosDesbloqueo: string[],
 * }} ContenidoPedagogico
 */

/**
 * @typedef {{
 *   id: string,
 *   slug: string,
 *   titulo: string,
 *   descripcion: string,
 *   dificultad: string,
 *   categoria: string,
 *   tiempoOrientativoMin: number|null,
 *   puntuacionXp: number|null,
 *   casosPrueba: CasoPruebaVisible[],
 *   etiquetas: string[],
 *   contenidoPedagogico: ContenidoPedagogico,
 * }} EjercicioPublico
 */

/**
 * @param {unknown} valor
 * @returns {string[]}
 */
function comoListaDeCadenas(valor) {
  return Array.isArray(valor) ? valor.map((elemento) => String(elemento)) : [];
}

/**
 * Mapea la respuesta cruda de `GET /exercises/{slug}` (serialización JSON de
 * `EjercicioPublico`, en snake_case) al tipo interno de frontend usado por
 * esta página (camelCase). Deliberadamente defensivo con `unknown`: no
 * confía en que la red entregue exactamente la forma esperada.
 * @param {unknown} datos
 * @returns {EjercicioPublico}
 */
export function mapearEjercicioPublico(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  const pedagogicoCrudo = /** @type {Record<string, unknown>} */ (crudo.contenido_pedagogico ?? {});
  const casosCrudos = Array.isArray(crudo.casos_prueba) ? crudo.casos_prueba : [];

  return {
    id: String(crudo.id ?? ''),
    slug: String(crudo.slug ?? ''),
    titulo: String(crudo.titulo ?? ''),
    descripcion: String(crudo.descripcion ?? ''),
    dificultad: String(crudo.dificultad ?? ''),
    categoria: String(crudo.categoria ?? ''),
    tiempoOrientativoMin:
      typeof crudo.tiempo_orientativo_min === 'number' ? crudo.tiempo_orientativo_min : null,
    puntuacionXp: typeof crudo.puntuacion_xp === 'number' ? crudo.puntuacion_xp : null,
    casosPrueba: casosCrudos.map((casoCrudo) => {
      const caso = /** @type {Record<string, unknown>} */ (casoCrudo ?? {});
      return {
        entrada: String(caso.entrada ?? ''),
        salidaEsperada: String(caso.salida_esperada ?? ''),
        esLimite: Boolean(caso.es_limite),
        descripcion: typeof caso.descripcion === 'string' ? caso.descripcion : null,
      };
    }),
    etiquetas: comoListaDeCadenas(crudo.etiquetas),
    contenidoPedagogico: {
      contexto: String(pedagogicoCrudo.contexto ?? ''),
      conceptosPrevios: comoListaDeCadenas(pedagogicoCrudo.conceptos_previos),
      objetivos: comoListaDeCadenas(pedagogicoCrudo.objetivos),
      restricciones: comoListaDeCadenas(pedagogicoCrudo.restricciones),
      ejemplos: comoListaDeCadenas(pedagogicoCrudo.ejemplos),
      erroresFrecuentes: comoListaDeCadenas(pedagogicoCrudo.errores_frecuentes),
      unidadDidactica:
        typeof pedagogicoCrudo.unidad_didactica === 'string' ? pedagogicoCrudo.unidad_didactica : null,
      competencias: comoListaDeCadenas(pedagogicoCrudo.competencias),
      requisitosDesbloqueo: comoListaDeCadenas(pedagogicoCrudo.requisitos_desbloqueo),
    },
  };
}

/**
 * Obtiene el detalle público de un ejercicio por su slug
 * (`GET /api/v1/exercises/{slug}`, `app.routers.v1.exercises.get_exercise`).
 * Propaga `ApiError` (ver `api-errors.js`) sin capturarla si la petición
 * falla (404, red, timeout, etc.): esta capa de servicio no decide cómo se
 * muestra el error, eso es responsabilidad de quien la llama (la página).
 * @param {string} slug
 * @param {{ signal?: AbortSignal }} [opciones]
 * @returns {Promise<EjercicioPublico>}
 */
export async function obtenerEjercicioPorSlug(slug, opciones = {}) {
  if (typeof slug !== 'string' || slug.trim().length === 0) {
    throw new TypeError('obtenerEjercicioPorSlug: "slug" debe ser una cadena no vacía');
  }
  const datos = await apiRequest(`/exercises/${encodeURIComponent(slug)}`, {
    signal: opciones.signal,
  });
  return mapearEjercicioPublico(datos);
}
